import { useLocation, Link } from "react-router-dom";
import { Mail, ShieldCheck, ArrowRight } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PreLaunchBanner from "@/components/redeem/PreLaunchBanner";

/**
 * AP-5B.2 — `/signin/sent` — confirmation page after a magic-link request.
 *
 * In preview debug mode (`ASSOSPAY_NATIVE_DEBUG_TOKEN_EXPOSE=true`),
 * the backend returns the raw `debug_token` so an operator can paste
 * the magic link without an email provider being configured. We carry
 * it via navigation state — NEVER via URL, localStorage, or
 * sessionStorage — and render an operator-only continue button that
 * navigates to `/signin/consume?token=...` (the consume page strips
 * the token from the URL on first render).
 *
 * Generic copy is identical whether the email exists or not.
 */
export default function SigninSent() {
  const location = useLocation();
  const debugToken = location?.state?.debugToken || null;
  const returnUrl = location?.state?.returnUrl || "/account";

  const consumeHref = debugToken
    ? `/signin/consume?token=${encodeURIComponent(debugToken)}&return_url=${encodeURIComponent(returnUrl)}`
    : null;

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="signin-sent-page">
      <Header />
      <div className="relative">
        <div className="spotlight absolute inset-0 -z-0 pointer-events-none" />
        <main className="relative z-10 max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="mb-6">
            <PreLaunchBanner />
          </div>

          <div className="glass rounded-md p-8" data-testid="signin-sent-card">
            <div className="flex items-center gap-3">
              <Mail className="h-6 w-6 text-[#00F0FF]" />
              <h1 className="font-display text-xl font-black uppercase tracking-[0.2em] text-white">
                Check your email
              </h1>
            </div>
            <p
              className="mt-3 font-mono text-xs text-white/70 max-w-lg"
              data-testid="signin-sent-copy"
            >
              If the email you entered can sign in, we just sent you a
              one-time link valid for 15 minutes. Open it on this device
              to land your AssosPay session.
            </p>

            <p className="mt-4 font-mono text-[11px] text-white/45">
              Didn't get it? The link is single-use and short-lived —
              you can request a fresh one any time.
            </p>

            <div className="mt-6 flex items-center gap-3">
              <Link
                to="/signin"
                replace
                className="btn-secondary text-xs"
                data-testid="signin-sent-back-link"
              >
                Send a new link
              </Link>
            </div>
          </div>

          {/* Preview-only operator continue panel.
              Rendered ONLY when the backend returned a `debug_token`,
              which itself only happens when ASSOSPAY_NATIVE_DEBUG_TOKEN_EXPOSE=true. */}
          {consumeHref && (
            <div
              className="mt-6 rounded-md border border-[#FFB800]/40 bg-[#FFB800]/5 p-5"
              data-testid="signin-debug-strip"
            >
              <p className="font-display text-[11px] uppercase tracking-[0.3em] text-[#FFB800]">
                Preview · operator-only
              </p>
              <p className="mt-2 font-mono text-[11px] text-white/65 leading-relaxed">
                No real email provider is wired in preview, so the
                magic link is exposed inline for the operator to click.
                This control disappears the moment a real provider
                ships and the debug flag is turned off.
              </p>
              <Link
                to={consumeHref}
                replace
                className="btn-primary text-xs mt-4 inline-flex items-center gap-2"
                data-testid="signin-debug-continue-button"
              >
                <ArrowRight className="h-3.5 w-3.5" />
                Continue with preview link
              </Link>
            </div>
          )}

          <div
            className="mt-6 flex items-center gap-2 text-white/45"
            data-testid="signin-sent-security-strip"
          >
            <ShieldCheck className="h-3.5 w-3.5 text-[#00F0FF]" />
            <span className="font-mono text-[11px]">
              No password · No wallet signature · One-time link only
            </span>
          </div>
        </main>
      </div>
      <Footer />
    </div>
  );
}
